import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './ApiError';

/**
 * Query client defaults.
 *
 * <p>The retry policy is the load-bearing setting. TanStack Query retries three times by
 * default, which is right for a flaky network and wrong for a `409 RIDE_FULL` -- the ride
 * will still be full on the third attempt, and each retry is a billed Lambda invocation
 * and a billed DynamoDB transaction. Retrying only what could plausibly succeed keeps
 * both the user experience and the bill honest.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            return error.isRetryable && failureCount < 2;
          }
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        // Ride availability changes underneath the user, so data goes stale quickly;
        // but not instantly, or every navigation refetches and pays for it.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        // Mutations are never retried automatically. A booking mutation carries an
        // idempotency key precisely so that *deliberate* retries are safe; an automatic
        // one would hide a real failure from the user at exactly the wrong moment.
        retry: false,
      },
    },
  });
}

/**
 * Query keys in one place, as a typed factory.
 *
 * <p>Ad-hoc key arrays scattered through components are how cache invalidation quietly
 * stops working: one place writes `['rides', id]` and another `['ride', id]`, and the
 * list never refreshes after an edit. A factory makes that a compile error.
 */
export const queryKeys = {
  currentUser: () => ['users', 'me'] as const,
  user: (userId: string) => ['users', userId] as const,
  userReviews: (userId: string) => ['users', userId, 'reviews'] as const,
  vehicles: () => ['vehicles'] as const,
  rides: {
    search: (params: Record<string, unknown>) => ['rides', 'search', params] as const,
    detail: (rideId: string) => ['rides', rideId] as const,
    mine: () => ['rides', 'mine'] as const,
    bookings: (rideId: string) => ['rides', rideId, 'bookings'] as const,
  },
  bookings: {
    list: () => ['bookings'] as const,
    detail: (bookingId: string) => ['bookings', bookingId] as const,
  },
  notifications: () => ['notifications'] as const,
  messages: (rideId: string, counterpartId: string) =>
    ['messages', rideId, counterpartId] as const,
} as const;
